/**
 * EditScraperModal Component
 *
 * Modal for editing existing scrapers
 */

import { useState, useEffect, useMemo } from "react";
import { FaServer, FaTimes, FaSpinner } from "react-icons/fa";
import { db, functions } from "../../firebase";
import { ScraperRepository } from "../../models/ScraperRepository";
import { useToast } from "../../components/shared";
import "./CreateScraperModal.scss"; // Reuse the same styles

export const EditScraperModal = ({ isOpen, onClose, scraper, onSuccess }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    scraperId: "",
    apiKey: "",
    platform: "local",
    availability: true,
    legacy: false,
    healthStatus: "unknown",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const scraperRepository = useMemo(
    () => new ScraperRepository(db, functions),
    [],
  );

  // Populate form when scraper changes
  useEffect(() => {
    if (scraper) {
      setFormData({
        name: scraper.name || "",
        url: scraper.url || "",
        scraperId: scraper.scraperId || "",
        apiKey: "", // Don't populate API key for security (leave blank unless user wants to change it)
        platform: scraper.platform || "local",
        availability: scraper.availability ?? true,
        legacy: scraper.legacy === true,
        healthStatus: scraper.healthStatus || "unknown",
        notes: scraper.notes || "",
      });
    }
  }, [scraper]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.url.trim() ||
      !formData.scraperId.trim()
    ) {
      return;
    }

    if (!scraper?.id) {
      toast.error("No scraper selected for editing");
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        url: formData.url.trim(),
        scraperId: formData.scraperId.trim(),
        platform: formData.platform,
        availability: formData.availability,
        legacy: formData.legacy,
        healthStatus: formData.healthStatus,
        notes: formData.notes.trim(),
      };

      // Only update API key if a new one was provided
      if (formData.apiKey.trim()) {
        updateData.apiKey = formData.apiKey.trim();
      }

      // Routes through the updateScraper Cloud Function (auth + field validation);
      // updatedAt is set server-side.
      await scraperRepository.update(scraper.id, updateData);

      toast.success("Scraper updated successfully");

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error updating scraper:", error);
      toast.error("Failed to update scraper");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-scraper-modal-overlay" onClick={handleClose}>
      <div
        className="create-scraper-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-scraper-modal__header">
            <div className="create-scraper-modal__title">
              <FaServer />
              Edit Scraper
            </div>
            <button
              type="button"
              className="create-scraper-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-scraper-modal__body">
            <p className="subtitle">
              Update scraper configuration. Changes to the Scraper ID will not
              affect existing scrapes linked to this scraper.
            </p>

            <div className="form-group">
              <label htmlFor="name">
                Scraper Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Railway Production Scraper"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">
                Scraper URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="e.g., https://scraper.example.com"
                required
                disabled={loading}
              />
              <span className="help-text">
                The full URL where this scraper service is hosted
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="scraperId">
                Scraper ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="scraperId"
                name="scraperId"
                value={formData.scraperId}
                onChange={handleChange}
                placeholder="e.g., railway-scraper-1"
                required
                disabled={loading}
              />
              <span className="help-text">
                Unique identifier matching the SCRAPER_ID environment variable
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="apiKey">
                API Key{" "}
                <span className="optional">
                  (optional - leave blank to keep existing)
                </span>
              </label>
              <input
                type="password"
                id="apiKey"
                name="apiKey"
                value={formData.apiKey}
                onChange={handleChange}
                placeholder="Enter new API key or leave blank to keep existing"
                disabled={loading}
              />
              <span className="help-text">
                Only provide a new API key if you want to change it
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="platform">
                Platform <span className="required">*</span>
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="local">Local Development</option>
                <option value="railway">Railway</option>
                <option value="vps">VPS</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="healthStatus">
                Health Status <span className="required">*</span>
              </label>
              <select
                id="healthStatus"
                name="healthStatus"
                value={formData.healthStatus}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="unknown">Unknown</option>
                <option value="healthy">Healthy</option>
                <option value="unhealthy">Unhealthy</option>
              </select>
              <span className="help-text">
                Current health status of the scraper
              </span>
            </div>

            <div className="form-group form-group--checkbox">
              <label htmlFor="availability">
                <input
                  type="checkbox"
                  id="availability"
                  name="availability"
                  checked={formData.availability}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Scraper is available for use</span>
              </label>
              <span className="help-text">
                When enabled, this scraper will be included in LRU routing
              </span>
            </div>

            <div className="form-group form-group--checkbox">
              <label htmlFor="legacy">
                <input
                  type="checkbox"
                  id="legacy"
                  name="legacy"
                  checked={formData.legacy}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Retired — keep for history only</span>
              </label>
              <span className="help-text">
                Hides it from the scraper list behind a &ldquo;show
                retired&rdquo; link. Nothing is deleted: past scrapes still
                reference it, so removing the record outright would leave those
                jobs pointing at nothing. Separate from availability, which is
                about whether a working scraper can be reached.
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="e.g., Running with all 5 proxies configured"
                rows="3"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.625rem 0.875rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "0.9375rem",
                  color: "#111827",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  resize: "vertical",
                }}
              />
              <span className="help-text">
                Additional information about this scraper
              </span>
            </div>
          </div>

          <div className="create-scraper-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                loading ||
                !formData.name.trim() ||
                !formData.url.trim() ||
                !formData.scraperId.trim()
              }
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
